import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, doc, getDoc, addDoc, serverTimestamp, query, where, orderBy } from 'firebase/firestore';
import axios from 'axios';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

class FirebaseService {
  /**
   * Get all available forms from Firebase io_forms collection
   * @returns {Promise<Array>}
   */
  async getForms() {
    try {
      const formsCollection = collection(db, 'io_forms');
      const formsSnapshot = await getDocs(formsCollection);
      const formsList = formsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        url: doc.data().url,
        ...doc.data()
      }));
      return formsList;
    } catch (error) {
      console.error('Error fetching forms from Firebase:', error);
      throw error;
    }
  }

  /**
   * Get a specific form by ID and fetch its JSON schema from the URL
   * @param {string} formId 
   * @returns {Promise<Object>}
   */
  async getFormById(formId) {
    try {
      // Get form metadata from Firebase
      const formDoc = doc(db, 'io_forms', formId);
      const formSnapshot = await getDoc(formDoc);
      
      if (!formSnapshot.exists()) {
        throw new Error('Form not found in database');
      }

      const formData = formSnapshot.data();
      
      // Fetch the actual form JSON from the URL
      if (!formData.url) {
        throw new Error('Form URL not found');
      }

      const response = await axios.get(formData.url);
      
      return {
        id: formSnapshot.id,
        name: formData.name,
        url: formData.url,
        schema: response.data, // The actual form JSON from the URL
        ...formData
      };
    } catch (error) {
      console.error('Error fetching form:', error);
      throw error;
    }
  }

  /**
   * Save form submission to Firebase form_answers collection
   * @param {string} formId - The ID of the form being submitted
   * @param {string} formName - The name of the form
   * @param {Object} submissionData - The form submission data
   * @returns {Promise<Object>}
   */
  async saveFormAnswer(formId, formName, submissionData) {
    try {
      const answersCollection = collection(db, 'form_answers');
      
      // Get user from localStorage
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      const answerDoc = {
        formId: formId,
        formName: formName,
        submission: submissionData,
        submittedAt: serverTimestamp(),
        userId: user?.id || user?.uid || user?.email || null,
        userEmail: user?.email || null,
        userName: user?.name || user?.displayName || null,
      };

      const docRef = await addDoc(answersCollection, answerDoc);
      
      return {
        success: true,
        id: docRef.id,
        message: 'Form answer saved successfully'
      };
    } catch (error) {
      console.error('Error saving form answer:', error);
      throw error;
    }
  }

  /**
   * Get all form submissions by a specific user
   * @param {string} userId - The ID of the user
   * @returns {Promise<Array>}
   */
  async getSubmissionsByUser(userId) {
    try {
      const answersCollection = collection(db, 'form_answers');
      const q = query(
        answersCollection, 
        where('userId', '==', userId),
        orderBy('submittedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const submissions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return submissions;
    } catch (error) {
      console.error('Error fetching user submissions:', error);
      throw error;
    }
  }
}

const firebaseService = new FirebaseService();
export default firebaseService;
